import React, { ReactElement } from 'react'
import cx from 'classnames'
import './_full-hero.css'

import BlockContent from 'src/components/global/BlockContent'

import { Serializer } from 'src/utils/serializer'
import { Image } from 'src/components/image'
import { useMediaQuery } from 'src/hooks/useMediaQuery'

type ImageMediaAsset = {
  _type: 'image'
  asset:
    | {
        _id: string
        _ref: string
      }
    | undefined
  _ref: unknown | undefined
}

type FileMediaAsset = {
  _type: 'file'
  asset:
    | {
        _id: string
        url: string
      }
    | undefined
}
type MediaAsset = ImageMediaAsset | FileMediaAsset

type FullBleedHeroProps = {
  data: {
    text: any[]
    backgroundColor?: string
    containerSize?: string
    spacing?: string
    desktopVideo: MediaAsset
    bgImage: ImageMediaAsset
    mobileBgImage: ImageMediaAsset
    brightnessPercentage?: number
  }
}

const FullHeroCanvas = ({
  bgImage,
  desktopVideoUrl,
  mobileImageIdentifier,
  desktopImageIdentifier,
  rawStyleForAllImages,
}: {
  desktopVideoUrl: string | undefined
  mobileImageIdentifier: string | undefined
  desktopImageIdentifier: string | undefined
  bgImage: ImageMediaAsset
  rawStyleForAllImages: React.CSSProperties
}) => {
  if (desktopVideoUrl) {
    return null
  }

  if (mobileImageIdentifier) {
    return null
  }

  if (!desktopImageIdentifier) {
    return null
  }

  return (
    <Image
      className="bg-image show__mobile unspecified__mobile"
      imageId={mobileImageIdentifier}
      alt={''}
      rawStyleOverride={rawStyleForAllImages}
      raw={bgImage}
      lazy
      fetchpriority="high"
    />
  )
}

const FullHeroView = ({
  desktopVideoUrl,
  mobileImageIdentifier,
  mobileBgImage,
  desktopImageIdentifier,
  bgImage,
  rawStyleForAllImages,
}: {
  desktopVideoUrl: string | undefined
  mobileImageIdentifier: string | undefined
  mobileBgImage: ImageMediaAsset
  desktopImageIdentifier: string | undefined
  bgImage: ImageMediaAsset
  rawStyleForAllImages: React.CSSProperties
}) => {
  const { isMobileDevice } = useMediaQuery()

  if (desktopVideoUrl && !isMobileDevice) {
    return (
      <div
        className="media__wrapper bg-image show__desktop"
        data-asktia-url={desktopVideoUrl}
      >
        <video autoPlay muted loop className="media__video">
          <source src={desktopVideoUrl} type="video/mp4" />
        </video>
      </div>
    )
  }

  const hasImage = !!(mobileImageIdentifier || desktopImageIdentifier)

  if (!hasImage) {
    return (
      <div className="unspecified__hero" data-asktia-mobidle={isMobileDevice}>
        &nbsp;
      </div>
    )
  }

  if (isMobileDevice) {
    const mobileImageId = mobileImageIdentifier || desktopImageIdentifier
    return (
      <Image
        className="bg-image show__mobile"
        imageId={mobileImageId}
        alt={''}
        rawStyleOverride={rawStyleForAllImages}
        raw={mobileBgImage}
        lazy
        fetchpriority="high"
      />
    )
  }

  const desktopImageId = desktopImageIdentifier || mobileImageIdentifier
  return (
    <Image
      className="bg-image show__desktop"
      imageId={desktopImageId}
      alt={''}
      rawStyleOverride={rawStyleForAllImages}
      raw={bgImage}
      lazy
      fetchpriority="high"
    />
  )
}

export const FullBleedHero = ({ data }: FullBleedHeroProps): ReactElement => {
  const {
    text,
    backgroundColor,
    containerSize,
    spacing,
    bgImage,
    desktopVideo,
    mobileBgImage,
    brightnessPercentage,
  } = data
  const desktopImageIdentifier = bgImage?.asset?._id || bgImage?.asset?._ref
  const mobileImageIdentifier =
    mobileBgImage?.asset?._id || mobileBgImage?.asset?._ref
  const brightnessCss = brightnessPercentage
    ? `brightness(${brightnessPercentage}%)`
    : 'brightness(100%)'
  const rawStyleForAllImages = {
    objectPosition: 'top center',
    filter: brightnessCss,
  }

  const desktopVideoUrl =
    desktopVideo?._type == 'file' ? desktopVideo?.asset?.url : undefined

  return (
    <div
      className={cx('viewport', 'rel', 'is-visible', backgroundColor, spacing, {
        cw: backgroundColor === 'bcb',
        pyl: spacing === undefined,
        'cw bcb': backgroundColor === undefined,
      })}
    >
      <FullHeroView
        mobileImageIdentifier={mobileImageIdentifier}
        mobileBgImage={mobileBgImage}
        desktopVideoUrl={desktopVideoUrl}
        desktopImageIdentifier={desktopImageIdentifier}
        bgImage={bgImage}
        rawStyleForAllImages={rawStyleForAllImages}
      />

      <FullHeroCanvas
        bgImage={bgImage}
        desktopVideoUrl={desktopVideoUrl}
        mobileImageIdentifier={mobileImageIdentifier}
        desktopImageIdentifier={desktopImageIdentifier}
        rawStyleForAllImages={rawStyleForAllImages}
      />

      <div
        className={cx('media__info', {
          media__context: 'undefined' !== desktopVideoUrl,
          'container--l outer hero__inner mxa f jcs ais py2 p1 al x rel':
            'undefined' === desktopVideoUrl,
        })}
      >
        <div
          className={cx('x animate opacity slide-up d2 col-m-7 col-xs-24', {
            'media__context--info': 'undefined' !== typeof desktopVideoUrl,
            [String(containerSize)]: Boolean(containerSize),
          })}
        >
          <div className="media__context--info--inner">
            <BlockContent blocks={text} serializers={Serializer} />
          </div>
        </div>
      </div>
    </div>
  )
}

export default FullBleedHero

export { ImageMediaAsset, FileMediaAsset, MediaAsset, FullBleedHeroProps }
